import React, { useContext, useEffect, useMemo, useState } from "react";
import { httpGet, httpPost } from "../../components/helpers/httpMethods";
import { UserDataContext } from "../../context/userContext";
import { PaymentContext } from "../../context/paymentContext";
import "./conApply.css";

// New Change of Name application (4 steps). Sends exactly the same fields to con/apply as the original form,
// then starts the same payment step (CONinitPayment). Progress is saved on this device.
const CATS = [
  { key: "Change of Name", title: "Change of Name", hint: "Taking a new name" },
  { key: "Change of Name/Age Declaration", title: "Change of Name / Age Declaration", hint: "New name and correct date of birth" },
  { key: "Confirmation of Name", title: "Confirmation of Name", hint: "Two versions of your name, same person" },
  { key: "Rearrangement of Name", title: "Rearrangement of Name", hint: "Same names, new order" },
];
const REASONS = [
  { key: "Marriage", label: "Marriage", proof: "Marriage certificate" },
  { key: "Faith", label: "Faith", proof: "e.g. baptism, naming or conversion certificate" },
  { key: "sworn_affidavit", label: "Personal choice (sworn affidavit)", proof: "" },
  { key: "Others", label: "Other", proof: "" },
];
const TITLES = ["Mr", "Mrs", "Miss", "Ms", "Dr", "Prof", "Chief", "Engr", "Barr", "Alhaji", "Alhaja", "Pastor", "Rev"];
const LABELS = {
  "Change of Name": ["Your former name", "Your new name"],
  "Change of Name/Age Declaration": ["Your former name", "Your new name"],
  "Confirmation of Name": ["The other version of your name", "Your name as it should be confirmed"],
  "Rearrangement of Name": ["Your name in the old order", "Your name in the new order"],
};
const naira = (v) => `₦${Math.round(Number(v) || 0).toLocaleString("en-NG")}`;
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : String(s || "").trim());
const nm = (...p) => p.map((x) => String(x || "").trim()).filter(Boolean).join(" ");
const longDate = (v) => { if (!v) return ""; const d = new Date(v); return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); };
const WEEK = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const earliest = (o) => {
  const n = parseInt(String(o.turnAroundTime || ""), 10); const d = new Date(); d.setDate(d.getDate() + (Number.isFinite(n) && n > 0 ? n : 3));
  let on = []; try { const a = Array.isArray(o.publishDays) ? o.publishDays : JSON.parse(o.publishDays || "[]"); on = a.map((s) => WEEK.indexOf(String(s).trim().toLowerCase())).filter((i) => i >= 0); } catch (e) { on = []; }
  for (let i = 0; i < 7 && on.length && !on.includes(d.getDay()); i += 1) d.setDate(d.getDate() + 1);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};
export const noticeFor = (f) => {
  const old = nm(f.formerTitle, f.formerFirstname, f.formerMiddlename, f.formerLastname) || "…";
  const neu = nm(f.newTitle, f.newFirstname, f.newMiddlename, f.newLastname) || "…";
  const end = " All former documents remain valid. General public please take note.";
  if (f.category === "Confirmation of Name") return { head: "CONFIRMATION OF NAME", body: `I, ${neu}, also known as ${old}, wish to confirm that both names refer to one and the same person.${end}` };
  if (f.category === "Rearrangement of Name") return { head: "REARRANGEMENT OF NAME", body: `I, formerly known and addressed as ${old}, now wish to be known and addressed as ${neu}.${end}` };
  if (f.category === "Change of Name/Age Declaration") return { head: "CHANGE OF NAME / AGE DECLARATION", body: `I, formerly known and addressed as ${old}, now wish to be known and addressed as ${neu}. My correct date of birth is ${longDate(f.newDateOfBirth) || "…"}${f.formalDateOfBirth ? ` and not ${longDate(f.formalDateOfBirth)}` : ""}.${end}` };
  return { head: "CHANGE OF NAME", body: `I, formerly known and addressed as ${old}, now wish to be known and addressed as ${neu}.${end}` };
};
// Phone photos are often 3-10 MB: shrink them in the browser before upload (faster on mobile data, never rejected for size)
const shrink = (file) => new Promise((resolve) => {
  if (!/^image\/(jpeg|png|webp)/.test(file.type) || file.size < 1.5 * 1024 * 1024) { resolve(file); return; }
  const img = new Image(); const url = URL.createObjectURL(file);
  img.onload = () => {
    const k = Math.min(1, 2000 / Math.max(img.width, img.height));
    const c = document.createElement("canvas"); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => { URL.revokeObjectURL(url); resolve(b && b.size < file.size ? new File([b], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file); }, "image/jpeg", 0.82);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});
const EMPTY = { category: "", reason: "", reasonDescription: "", formerTitle: "", formerFirstname: "", formerMiddlename: "", formerLastname: "",
  newTitle: "", newFirstname: "", newMiddlename: "", newLastname: "", formalDateOfBirth: "", newDateOfBirth: "" };

export default function ConApply(props) {
  const { history } = props;
  const publicationId = props.match.params.id;
  const user = useContext(UserDataContext) || {};
  const pay = useContext(PaymentContext) || {};
  const { UserInfo } = user;
  const draftKey = `tns-con-draft:${publicationId}`;
  const [offer, setOffer] = useState(undefined);
  const [f, setF] = useState(() => {
    try { const d = JSON.parse(localStorage.getItem(draftKey) || "null"); if (d && d.f) return { ...EMPTY, ...d.f }; } catch (e) { /* ignore */ }
    let cat = ""; try { cat = localStorage.getItem("tns-con-category") || ""; } catch (e) { /* ignore */ }
    return { ...EMPTY, category: CATS.some((c) => c.key === cat) ? cat : "" };
  });
  const [step, setStep] = useState(() => { try { const d = JSON.parse(localStorage.getItem(draftKey) || "null"); return d && d.step ? Math.min(d.step, 3) : 1; } catch (e) { return 1; } });
  const [files, setFiles] = useState({ affidavit: null, proveOfReason: null, idCard: null });
  const [err, setErr] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [restored] = useState(() => { try { return !!localStorage.getItem(draftKey); } catch (e) { return false; } });

  useEffect(() => {
    httpGet("con/vendors").then((r) => {
      const list = (r && r.data && r.data.conSubscriptions) || [];
      setOffer(list.find((o) => String(o.publicationId) === String(publicationId)) || null);
    });
    window.scrollTo(0, 0);
  }, [publicationId]);
  useEffect(() => { try { localStorage.setItem(draftKey, JSON.stringify({ f, step, t: Date.now() })); } catch (e) { /* ignore */ } }, [f, step]);

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErr(""); };
  const ageDecl = f.category === "Change of Name/Age Declaration";
  const reason = REASONS.find((r) => r.key === f.reason);
  const labels = LABELS[f.category] || LABELS["Change of Name"];
  const notice = useMemo(() => noticeFor(f), [f]);
  const total = offer ? Number(offer.price) + Number(offer.serviceCharge || 0) : 0;

  const check = (s) => {
    if (s >= 1) {
      if (!f.category) return "Please choose what kind of notice you need.";
      if (!f.reason) return "Please choose the reason for your change.";
      if (f.reason === "Others" && !f.reasonDescription.trim()) return "Please tell us the reason for your change.";
    }
    if (s >= 2) {
      if (!f.formerTitle.trim() || !f.formerFirstname.trim() || !f.formerLastname.trim()) return `${labels[0]}: title, first name and surname are required.`;
      if (!f.newTitle.trim() || !f.newFirstname.trim() || !f.newLastname.trim()) return `${labels[1]}: title, first name and surname are required.`;
      if (!ageDecl && nm(f.formerFirstname, f.formerMiddlename, f.formerLastname).toLowerCase() === nm(f.newFirstname, f.newMiddlename, f.newLastname).toLowerCase())
        return f.category === "Rearrangement of Name" ? "The new order is the same as the old one. Please change the order of your names."
          : f.category === "Confirmation of Name" ? "Both versions of your name are the same. Please enter the other version of your name." : "Your new name is the same as your former name.";
      if (f.category === "Rearrangement of Name") {
        const w = (x) => x.toLowerCase().split(/\s+/).filter(Boolean).sort().join(" ");
        if (w(nm(f.formerFirstname, f.formerMiddlename, f.formerLastname)) !== w(nm(f.newFirstname, f.newMiddlename, f.newLastname)))
          return "A rearrangement uses the same names in a different order. To change a name, choose Change of Name.";
      }
      if (ageDecl && !f.formalDateOfBirth) return "Please enter the date of birth on your old documents.";
      if (ageDecl && !f.newDateOfBirth) return "Please enter your correct date of birth.";
    }
    if (s >= 3 && !files.affidavit && !files.proveOfReason) return "Please upload your sworn affidavit (or a proof document).";
    return "";
  };
  const next = () => { const e = check(step); if (e) { setErr(e); return; } setErr(""); setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setErr(""); setStep(Math.max(1, step - 1)); };
  const pick = (k) => async (e) => {
    const x0 = e.target.files && e.target.files[0];
    if (!x0) return;
    if (!/^image\/|application\/pdf/.test(x0.type)) { setErr("Please choose a photo (JPG or PNG) or a PDF."); return; }
    if (x0.size > 50 * 1024 * 1024) { setErr("That file is too large. Please choose one under 50 MB."); return; }
    const x = await shrink(x0);
    if (x.size > 50 * 1024 * 1024) { setErr("That file is over 50 MB. Please upload a photo of the document instead."); return; }
    setErr(""); setFiles((f0) => ({ ...f0, [k]: x }));
  };

  const submit = async () => {
    const e = check(3); if (e) { setErr(e); setStep(e.includes("upload") ? 3 : e.includes("name") || e.includes("birth") ? 2 : 1); return; }
    if (!agree) { setErr("Please confirm the details are correct."); return; }
    if (!UserInfo || !UserInfo.email) { setErr("Please log in to submit your application."); return; }
    setBusy(true); setErr("");
    const desc = [`Category: ${f.category}`, f.reasonDescription.trim()].filter(Boolean).join(". ");
    const Data = new FormData();
    Data.append("formerTitle", f.formerTitle.trim());
    Data.append("reason", f.reason);
    Data.append("formerLastname", f.formerLastname.trim());
    Data.append("formerFirstname", f.formerFirstname.trim());
    Data.append("formerMiddlename", f.formerMiddlename.trim());
    Data.append("newTitle", f.newTitle.trim());
    Data.append("newLastname", f.newLastname.trim());
    Data.append("newFirstname", f.newFirstname.trim());
    Data.append("newMiddlename", f.newMiddlename.trim());
    if (files.affidavit) Data.append("affidavit", files.affidavit);
    Data.append("reasonDescription", desc);
    Data.append("publicationId", publicationId);
    Data.append("email", UserInfo.email);
    Data.append("phone", `${UserInfo.countryCode || ""}${UserInfo.phoneNumber || ""}`);
    if (files.proveOfReason) Data.append("proveOfReason", files.proveOfReason);
    if (ageDecl) { Data.append("formalDateOfBirth", f.formalDateOfBirth); Data.append("newDateOfBirth", f.newDateOfBirth); }
    let res;
    try {
      res = await Promise.race([httpPost("con/apply", Data), new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 90000))]);
    } catch (x) {
      setBusy(false);
      setErr(x && x.message === "timeout" ? "This is taking too long. Your details are saved; please check your connection and try again." : "We couldn’t reach the server. Your details are saved; please try again.");
      return;
    }
    if (!res || res.code !== 201 || !res.data || !res.data.conApplication) {
      setBusy(false);
      setErr((res && res.er && (res.er.message || String(res.er))) || "We couldn’t submit your application. Please try again.");
      return;
    }
    try { localStorage.removeItem(draftKey); } catch (x) { /* ignore */ }
    const app = res.data.conApplication;
    pay.CONinitPayment({ publicationId: app.publicationId || publicationId, id: app.id, currency: "NGN", amount: `${total}`, type: "CARD" });
    setBusy(false);
  };

  if (offer === undefined) return <div className="ca-page"><p className="ca-muted">Loading…</p></div>;
  if (!offer) return <div className="ca-page"><div className="ca-card"><p>This newspaper isn’t taking Change of Name notices right now.</p><button type="button" className="ca-btn" onClick={() => history.push("/changeofname")}>Choose another newspaper</button></div></div>;

  const Field = ({ k, label, opt, type = "text", list }) => (
    <label className="ca-field"><span>{label}{opt ? <em> (optional)</em> : null}</span>
      <input className="ca-input" type={type} value={f[k]} onChange={set(k)} list={list} autoComplete="off" />
    </label>
  );
  const Names = ({ p, title }) => (
    <div className="ca-names">
      <h3>{title}</h3>
      <div className="ca-grid4">
        {Field({ k: `${p}Title`, label: "Title", list: "ca-titles" })}
        {Field({ k: `${p}Firstname`, label: "First name" })}
        {Field({ k: `${p}Middlename`, label: "Middle name", opt: true })}
        {Field({ k: `${p}Lastname`, label: "Surname" })}
      </div>
    </div>
  );
  const Doc = ({ k, title, note, need }) => (
    <div className={`ca-card ca-doc${files[k] ? " done" : ""}`}>
      <span className="ca-doc-icon" aria-hidden="true">{files[k] ? "✓" : "⬆"}</span>
      <span className="ca-doc-text"><strong>{title} <em>· {need}</em></strong><small>{files[k] ? `${files[k].name} · ${(files[k].size / 1048576).toFixed(1)} MB` : note}</small></span>
      <label className="ca-btn2 ca-file">{files[k] ? "Replace" : "Choose file"}<input type="file" accept="image/*,application/pdf" onChange={pick(k)} /></label>
    </div>
  );

  return (
    <div className="ca-page">
      <datalist id="ca-titles">{TITLES.map((t) => <option key={t} value={t} />)}</datalist>
      <div className="ca-top"><h1>Change of Name application</h1><span className="ca-saved">✓ Saved on this device · you can continue later</span></div>
      {restored && step === 1 && f.formerFirstname ? <div className="ca-restore">Welcome back – we kept your details. Documents need to be added again.</div> : null}
      <ol className="ca-steps">
        {["Your notice", "Your details", "Documents", "Review and pay"].map((s, i) => (
          <li key={s} className={i + 1 < step ? "done" : i + 1 === step ? "on" : ""}><span>{i + 1 < step ? "✓" : i + 1}</span>{s}</li>
        ))}
      </ol>
      <div className="ca-layout">
        <div className="ca-main">
          {err ? <p className="ca-err" role="alert">{err}</p> : null}

          {step === 1 ? (
            <>
              <section className="ca-card">
                <h2>What kind of notice?</h2>
                <div className="ca-choices">
                  {CATS.map((c) => (
                    <button type="button" key={c.key} className={`ca-choice${f.category === c.key ? " on" : ""}`} aria-pressed={f.category === c.key} onClick={() => { setF({ ...f, category: c.key }); setErr(""); }}>
                      <strong>{c.title}</strong><small>{c.hint}</small>
                    </button>
                  ))}
                </div>
              </section>
              <section className="ca-card">
                <h2>Why are you making this change?</h2>
                <div className="ca-chips">
                  {REASONS.map((r) => <button type="button" key={r.key} className={f.reason === r.key ? "on" : ""} aria-pressed={f.reason === r.key} onClick={() => { setF({ ...f, reason: r.key }); setErr(""); }}>{r.label}</button>)}
                </div>
                {f.reason === "Others" ? <label className="ca-field"><span>Tell us the reason</span><textarea className="ca-input ca-area" value={f.reasonDescription} onChange={set("reasonDescription")} maxLength={500} /></label> : null}
              </section>
            </>
          ) : null}

          {step === 2 ? (
            <section className="ca-card">
              {Names({ p: "former", title: labels[0] })}
              {Names({ p: "new", title: labels[1] })}
              {ageDecl ? (
                <div className="ca-names"><h3>Date of birth</h3>
                  <div className="ca-grid2">
                    {Field({ k: "formalDateOfBirth", label: "Date of birth on your old documents", type: "date" })}
                    {Field({ k: "newDateOfBirth", label: "Your correct date of birth", type: "date" })}
                  </div>
                </div>
              ) : null}
              {f.reason === "Marriage" && f.category === "Change of Name" ? <p className="ca-tip">Tip: many people keep their maiden name as a middle name, e.g. Adaeze Ngozi Okafor-Bello.</p> : null}
            </section>
          ) : null}

          {step === 3 ? (
            <>
              <section className="ca-card"><h2>Upload your documents</h2><p className="ca-muted">A clear photo or PDF of each. Photos are made smaller automatically. The newspaper checks them against your notice before printing.</p></section>
              {Doc({ k: "affidavit", title: "Sworn affidavit", need: "required", note: "Your sworn affidavit of change of name from a court registry or commissioner for oaths." })}
              {reason && reason.proof ? Doc({ k: "proveOfReason", title: reason.key === "Marriage" ? "Marriage certificate" : "Proof of your reason", need: "recommended", note: reason.proof }) : null}
              
              <div className="ca-card ca-hint"><strong>Don’t have an affidavit yet?</strong> Get one from a High Court registry or a commissioner for oaths. Your details are saved; come back when you have it.</div>
            </>
          ) : null}

          {step === 4 ? (
            <>
              <section className="ca-card">
                <h2>Check everything</h2>
                <dl className="ca-review">
                  <dt>Notice</dt><dd>{(CATS.find((c) => c.key === f.category) || {}).title}</dd>
                  <dt>Reason</dt><dd>{reason ? reason.label : ""}{f.reasonDescription ? ` – ${f.reasonDescription}` : ""}</dd>
                  <dt>{labels[0]}</dt><dd>{nm(f.formerTitle, f.formerFirstname, f.formerMiddlename, f.formerLastname)}</dd>
                  <dt>{labels[1]}</dt><dd>{nm(f.newTitle, f.newFirstname, f.newMiddlename, f.newLastname)}</dd>
                  {ageDecl ? <><dt>Date of birth</dt><dd>{longDate(f.newDateOfBirth)} (old documents: {longDate(f.formalDateOfBirth)})</dd></> : null}
                  <dt>Documents</dt><dd>{["affidavit", "proveOfReason", "idCard"].filter((k) => files[k]).map((k) => ({ affidavit: "Affidavit", proveOfReason: "Proof", idCard: "ID" }[k])).join(" · ") || "None"} ✓</dd>
                  <dt>Newspaper</dt><dd>{titleCase(offer.publication && offer.publication.name)} · earliest {earliest(offer)}</dd>
                </dl>
                <button type="button" className="ca-link" onClick={() => setStep(2)}>Edit details</button>
              </section>
              <label className="ca-card ca-agree"><input type="checkbox" checked={agree} onChange={(e) => { setAgree(e.target.checked); setErr(""); }} /><span>I confirm the names, dates and documents are correct, and that I am the person named (or authorised to apply for them).</span></label>
            </>
          ) : null}

          <div className="ca-nav">
            {step > 1 ? <button type="button" className="ca-btn2" onClick={back}>Back</button> : <button type="button" className="ca-btn2" onClick={() => history.push("/changeofname")}>Cancel</button>}
            {step < 4 ? <button type="button" className="ca-btn" onClick={next}>{["", "Continue to your details", "Continue to documents", "Continue to review"][step]}</button>
              : <button type="button" className="ca-btn ca-pay" onClick={submit} disabled={busy}>{busy ? "Submitting…" : `Pay ${naira(total)} and submit`}</button>}
          </div>
        </div>

        <aside className="ca-side">
          <div className="ca-card ca-paper">
            <span className="ca-logo">{offer.publication && (offer.publication.logo || offer.publication.image) ? <img src={offer.publication.logo || offer.publication.image} alt="" /> : null}</span>
            <span className="ca-paper-text"><strong>{titleCase(offer.publication && offer.publication.name)}</strong><small>Earliest: {earliest(offer)} · {naira(total)} incl. service</small></span>
            <button type="button" className="ca-link" onClick={() => history.push("/changeofname")}>Change</button>
          </div>
          <div className="ca-card ca-notice">
            <span className="ca-kicker">Your notice</span>
            <div className="ca-print"><b>{notice.head}</b><br />{notice.body}</div>
            <small>Updates as you type. This is the wording we send to the newspaper, so check every spelling.</small>
          </div>
          {step === 4 ? (
            <div className="ca-card ca-price">
              <span className="ca-kicker">To pay</span>
              <div><span>{titleCase(offer.publication && offer.publication.name)} notice</span></div>
              <div className="ca-total"><span>Total</span><span>{naira(total)}</span></div>
              <small>Card, bank transfer or USSD with Paystack. Track it any time in My applications.</small>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
